import { supabase } from '../lib/supabase';

export const workflowService = {
  async getWorkflows(userId) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: null };
      const { data, error } = await supabase
        ?.from('workflows')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });
      if (error) {
        console.warn('Error fetching workflows:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getWorkflows:', err);
      return { data: [], error: err?.message };
    }
  },

  async getWorkflowById(workflowId) {
    if (!workflowId) return { data: null, error: 'Workflow ID required' };
    try {
      if (!supabase) return { data: null, error: null };
      const { data, error } = await supabase
        ?.from('workflows')
        ?.select('*')
        ?.eq('id', workflowId)
        ?.single();
      if (error && error?.code !== 'PGRST116') {
        return { data: null, error: error?.message };
      }
      return { data: data || null, error: null };
    } catch (err) {
      console.error('Error in getWorkflowById:', err);
      return { data: null, error: err?.message };
    }
  },

  async createWorkflow(workflowData) {
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('workflows')
        ?.insert([{
          name: workflowData?.name || 'New Workflow',
          description: workflowData?.description || '',
          user_id: workflowData?.user_id,
          status: workflowData?.status || 'draft',
          trigger_type: workflowData?.trigger_type || 'manual',
          steps: workflowData?.steps || [],
          settings: workflowData?.settings || {},
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        }])
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error creating workflow:', err);
      return { data: null, error: err?.message };
    }
  },

  async updateWorkflow(workflowId, updates) {
    if (!workflowId) return { data: null, error: 'Workflow ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('workflows')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', workflowId)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error updating workflow:', err);
      return { data: null, error: err?.message };
    }
  },

  async deleteWorkflow(workflowId) {
    if (!workflowId) return { success: false, error: 'Workflow ID required' };
    try {
      if (!supabase) return { success: false, error: 'Database not available' };
      const { error } = await supabase
        ?.from('workflows')
        ?.delete()
        ?.eq('id', workflowId);
      if (error) throw error;
      return { success: true, error: null };
    } catch (err) {
      console.error('Error deleting workflow:', err);
      return { success: false, error: err?.message };
    }
  },

  async getWorkflowAnalytics(userId) {
    if (!userId) return { data: null, error: 'User ID required' };
    try {
      if (!supabase) return { data: { total: 0, active: 0, paused: 0, draft: 0 }, error: null };
      const { data, error } = await supabase
        ?.from('workflows')
        ?.select('id, status')
        ?.eq('user_id', userId);
      if (error) {
        return { data: { total: 0, active: 0, paused: 0, draft: 0 }, error: null };
      }
      const workflows = data || [];
      return {
        data: {
          total: workflows?.length,
          active: workflows?.filter(w => w?.status === 'active')?.length,
          paused: workflows?.filter(w => w?.status === 'paused')?.length,
          draft: workflows?.filter(w => w?.status === 'draft')?.length
        },
        error: null
      };
    } catch (err) {
      console.error('Error in getWorkflowAnalytics:', err);
      return { data: null, error: err?.message };
    }
  }
};

export default workflowService;
